<template>
  <div class="catalog-item-search position-relative" ref="root">
    <input type="text"
           class="form-control form-control-solid"
           :class="[size === 'sm' ? 'form-control-sm' : '', inputClass]"
           :value="query"
           :placeholder="placeholder"
           autocomplete="off"
           @input="onInput"
           @focus="onFocus"
           @blur="onBlur"
           @keydown.esc="close"
           @keydown.down.prevent="moveHighlight(1)"
           @keydown.up.prevent="moveHighlight(-1)"
           @keydown.enter.prevent="confirmHighlight">

    <ul v-if="open && items.length"
        class="catalog-item-search__menu list-unstyled mb-0 shadow-sm border rounded bg-white">
      <li v-for="(item, index) in items"
          :key="item.id_item"
          class="catalog-item-search__option px-3 py-2 fs-7"
          :class="{ 'catalog-item-search__option--active': index === highlightIndex }"
          @mousedown.prevent="selectItem(item)">
        {{ itemLabel(item) }}
      </li>
    </ul>
  </div>
</template>

<script>
import http from '@/helpers/http';
import debounce from '@/helpers/debounce';

export default {
  name: 'InputSearchCatalogItem',
  props: {
    modelValue: { type: [Number, String], default: null },
    displayValue: { type: String, default: '' },
    itemType: { type: String, default: null },
    placeholder: { type: String, default: 'Buscar item no catálogo' },
    size: { type: String, default: '' },
    inputClass: { type: String, default: '' },
  },
  emits: ['update:modelValue', 'select', 'clear'],
  data() {
    return {
      query: '',
      items: [],
      open: false,
      highlightIndex: -1,
      suppressSearch: false,
    };
  },
  computed: {
    place() {
      return this.$store.state.auth.place;
    },
  },
  watch: {
    displayValue: {
      immediate: true,
      handler(value) {
        if (!this.modelValue) {
          return;
        }

        if (value !== this.query) {
          this.query = value;
        }
      },
    },
    modelValue(value) {
      if (value || this.query) {
        return;
      }

      this.query = '';
      this.items = [];
      this.close();
    },
  },
  created() {
    this.loadItems = debounce(this.fetchItems, 350);
  },
  methods: {
    itemLabel(item) {
      const exam = item?.exam;
      if (exam) {
        return [exam.exam_tuss_code, exam.exam_name].filter(Boolean).join(' - ');
      }

      return item?.item_name ?? '';
    },
    onInput(event) {
      this.query = event.target.value;
      this.suppressSearch = false;

      if (this.modelValue) {
        this.$emit('clear');
      }

      this.$emit('update:modelValue', null);
      this.highlightIndex = -1;

      if (!this.query.trim()) {
        this.items = [];
        this.close();
        return;
      }

      this.open = true;
      this.loadItems();
    },
    onFocus() {
      if (this.items.length) {
        this.open = true;
      }
    },
    onBlur() {
      setTimeout(() => {
        this.close();
      }, 150);
    },
    fetchItems() {
      if (this.suppressSearch || !this.place?.id_place || !this.query.trim()) {
        return;
      }

      const params = {
        search: this.query,
        per_page: 50,
      };

      if (this.itemType) {
        params.item_type = this.itemType;
      }

      http.get(`/places/${this.place.id_place}/items`, { params }).then((response) => {
        this.items = response.data?.data ?? response.data ?? [];
        this.open = this.items.length > 0;
        this.highlightIndex = this.items.length ? 0 : -1;
      });
    },
    selectItem(item) {
      this.suppressSearch = true;
      this.query = this.itemLabel(item);
      this.$emit('update:modelValue', item.id_item);
      this.$emit('select', item);
      this.items = [];
      this.close();
    },
    moveHighlight(step) {
      if (!this.open || !this.items.length) {
        return;
      }

      const total = this.items.length;
      const next = this.highlightIndex < 0 ? 0 : (this.highlightIndex + step + total) % total;
      this.highlightIndex = next;
    },
    confirmHighlight() {
      if (!this.open || this.highlightIndex < 0 || !this.items[this.highlightIndex]) {
        return;
      }

      this.selectItem(this.items[this.highlightIndex]);
    },
    close() {
      this.open = false;
      this.highlightIndex = -1;
    },
    setSelection(item) {
      if (!item?.id_item) {
        return;
      }

      this.selectItem(item);
    },
  },
};
</script>

<style scoped>
.catalog-item-search__menu {
  position: absolute;
  top: calc(100% + 2px);
  left: 0;
  right: 0;
  z-index: 1060;
  max-height: 220px;
  overflow-y: auto;
}

.catalog-item-search__option {
  cursor: pointer;
}

.catalog-item-search__option:hover,
.catalog-item-search__option--active {
  background: var(--bs-light);
}
</style>
